import { useState, useEffect } from "react";

const N = {
  bg: "#0f0f0f", surface: "#1a1a1a", border: "#2a2a2a", borderHover: "#3a3a3a",
  text: "#f0f0f0", muted: "#777",
  uni:    { neon: "#00f5ff", dim: "#002b2e", border: "#00c8d4" },
  inv:    { neon: "#39ff14", dim: "#0a1f00", border: "#28cc0f" },
  gym:    { neon: "#ff6fff", dim: "#200020", border: "#cc44cc" },
  streak: { neon: "#ffae00", dim: "#1f1300", border: "#cc8800" },
  sleep:  { neon: "#a78bfa", dim: "#140d2e", border: "#6d4fc2" },
};

const BASE_CATS = {
  "Universidad":            N.uni,
  "Inversiones & Negocios": N.inv,
  "Gimnasio":               N.gym,
  "Mejora Personal":        N.streak,
};
let CAT = { ...BASE_CATS };

const MUSCLE_GROUPS = ["Push", "Pull", "Legs"];
const WEEK_DAYS  = ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"];
const DAYS_SHORT = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];
const NEON_POOL  = ["#ff4d4d","#4dffb8","#ff9f43","#a29bfe","#fd79a8","#74b9ff","#55efc4"];

const DEFAULT_HABITS = [
  { id: 1, name: "Repasar apuntes",       category: "Universidad" },
  { id: 2, name: "Entregar tareas",        category: "Universidad" },
  { id: 3, name: "Revisar portafolio",     category: "Inversiones & Negocios" },
  { id: 4, name: "Leer noticias de mercado", category: "Inversiones & Negocios" },
];

function todayStr() { return new Date().toISOString().slice(0, 10); }
function fmtDate(str) {
  const [y, m, d] = str.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}
function monthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function daysInMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}
function weekDates() {
  const t = new Date(), day = t.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(t);
    d.setDate(t.getDate() + diff + i);
    return d.toISOString().slice(0, 10);
  });
}
function computeStreak(completions, id) {
  let s = 0;
  const d = new Date();
  while (true) {
    const k = d.toISOString().slice(0, 10);
    if (completions[k]?.[id]) { s++; d.setDate(d.getDate() - 1); }
    else break;
  }
  return s;
}

const pill = (col) => ({
  fontSize: 11, padding: "2px 9px", borderRadius: 20,
  background: col.dim, color: col.neon, border: `0.5px solid ${col.border}`,
  display: "inline-block", whiteSpace: "nowrap",
});
const abtn = (col) => ({
  background: col.dim, border: `0.5px solid ${col.border}`,
  color: col.neon, borderRadius: 8, padding: "7px 14px",
  fontSize: 13, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
});

function HoverRow({ children, onDelete, style = {} }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ position: "relative", ...style }}
    >
      {children}
      {hov && (
        <button
          onClick={onDelete}
          style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            width: 22, height: 22, borderRadius: "50%",
            background: "#7a0000", border: "1.5px solid #e24b4a", color: "#f09595",
            cursor: "pointer", fontSize: 13, display: "flex",
            alignItems: "center", justifyContent: "center", padding: 0,
          }}
        >×</button>
      )}
    </div>
  );
}

function SleepChart({ data }) {
  if (!data.length) {
    return <p style={{ fontSize: 13, color: N.muted }}>Sin datos aún.</p>;
  }
  const W = 500, H = 140, PL = 28, PR = 8, PT = 10, PB = 24;
  const cW = W - PL - PR, cH = H - PT - PB;
  const maxH = Math.max(...data.map(d => d.h), 10);
  const xs = data.map((_, i) => PL + i * (cW / Math.max(data.length - 1, 1)));
  const ys = data.map(d => PT + cH - (d.h / maxH) * cH);
  const pts = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
  const n = data.length;
  const sx = xs.reduce((a, x) => a + x, 0);
  const sy = ys.reduce((a, y) => a + y, 0);
  const sxy = xs.reduce((a, x, i) => a + x * ys[i], 0);
  const sxx = xs.reduce((a, x) => a + x * x, 0);
  const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
  const intercept = (sy - slope * sx) / n;
  const ty1 = slope * xs[0] + intercept;
  const ty2 = slope * xs[xs.length - 1] + intercept;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {[0, Math.round(maxH / 2), maxH].map(v => {
        const y = PT + cH - (v / maxH) * cH;
        return (
          <g key={v}>
            <line x1={PL} y1={y} x2={W - PR} y2={y} stroke={N.border} strokeWidth="0.5" />
            <text x={PL - 3} y={y + 4} textAnchor="end" fontSize="8" fill={N.muted}>{v}</text>
          </g>
        );
      })}
      {data.length > 1 && (
        <line x1={xs[0]} y1={ty1} x2={xs[xs.length - 1]} y2={ty2}
          stroke={N.streak.neon} strokeWidth="1.5" strokeDasharray="5,3" opacity="0.7" />
      )}
      <polyline points={pts} fill="none" stroke={N.uni.neon} strokeWidth="1.5" />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={xs[i]} cy={ys[i]} r="3" fill={N.uni.neon} />
          {(i === 0 || i === data.length - 1 || data.length <= 10) && (
            <text x={xs[i]} y={H - 4} textAnchor="middle" fontSize="7" fill={N.muted}>{d.day}</text>
          )}
        </g>
      ))}
    </svg>
  );
}

export default function App() {
  const [tab, setTab]             = useState("dashboard");
  const [habits, setHabits]       = useState(DEFAULT_HABITS);
  const [completions, setComp]    = useState({});
  const [gymSessions, setGym]     = useState([]);
  const [activeSession, setAS]    = useState(null);
  const [newSessForm, setNSF]     = useState({ muscle: MUSCLE_GROUPS[0], date: todayStr() });
  const [showNewSess, setSNS]     = useState(false);
  const [newExercise, setNE]      = useState({ nombre: "", series: [{ peso: "", reps: "" }] });
  const [gymView, setGymView]     = useState("historial");
  const [schedule, setSched]      = useState({});
  const [editSlot, setEditSlot]   = useState(null);
  const [slotDraft, setSD]        = useState({ label: "", start: "", end: "" });
  const [ramos, setRamos]         = useState([]);
  const [newRamo, setNewRamo]     = useState("");
  const [showAddRamo, setSAR]     = useState(false);
  const [newTaskTxt, setNTT]      = useState({});
  const [showAddTask, setSAT]     = useState({});
  const [sleepLog, setSleep]      = useState({});
  const [sleepIn, setSleepIn]     = useState("");
  const [categories, setCats]     = useState(Object.keys(BASE_CATS));
  const [newCatName, setNCN]      = useState("");
  const [showAddCat, setSAC]      = useState(false);
  const [newHabit, setNewHabit]   = useState({ name: "", category: Object.keys(BASE_CATS)[0] });
  const [filterCat, setFilter]    = useState("Todas");
  const [nextId, setNextId]       = useState(10);

  useEffect(() => {
    (async () => {
      try {
        const load = async (k) => {
          try {
            const r = await window.storage?.get(k);
            return r ? JSON.parse(r.value) : null;
          } catch { return null; }
        };
        const cats = await load("v6_cats");
        if (cats) {
          setCats(cats);
          cats.forEach(c => {
            if (!CAT[c]) CAT[c] = { neon: NEON_POOL[Object.keys(CAT).length % NEON_POOL.length], dim: "#111", border: "#555" };
          });
        }
        const h  = await load("v6_habits"); if (h)  setHabits(h);
        const c  = await load("v6_comp");   if (c)  setComp(c);
        const g  = await load("v6_gym");    if (g)  setGym(g);
        const s  = await load("v6_sched");  if (s)  setSched(s);
        const r  = await load("v6_ramos");  if (r)  setRamos(r);
        const sl = await load("v6_sleep");  if (sl) setSleep(sl);
        const ni = await load("v6_nid");    if (ni) setNextId(ni);
      } catch {}
    })();
  }, []);

  const sv = async (k, v) => {
    try { await window.storage?.set(k, JSON.stringify(v)); } catch {}
  };

  const today = todayStr();
  const week  = weekDates();
  const mk    = monthKey();

  // ── habits
  function toggleHabit(id) {
    setComp(prev => {
      const next = { ...prev, [today]: { ...(prev[today] || {}), [id]: !prev[today]?.[id] } };
      sv("v6_comp", next);
      return next;
    });
  }
  function addHabit() {
    if (!newHabit.name.trim()) return;
    const h = [...habits, { id: nextId, name: newHabit.name.trim(), category: newHabit.category }];
    setHabits(h); sv("v6_habits", h);
    const nid = nextId + 1; setNextId(nid); sv("v6_nid", nid);
    setNewHabit({ name: "", category: categories[0] });
  }
  function removeHabit(id) {
    const h = habits.filter(x => x.id !== id);
    setHabits(h); sv("v6_habits", h);
  }

  // ── categories
  function addCategory() {
    const name = newCatName.trim();
    if (!name || categories.includes(name)) return;
    const neon = NEON_POOL[categories.length % NEON_POOL.length];
    CAT[name] = { neon, dim: "#111", border: neon + "88" };
    const updated = [...categories, name];
    setCats(updated); sv("v6_cats", updated);
    setNCN(""); setSAC(false);
  }
  function removeCategory(cat) {
    if (BASE_CATS[cat]) return;
    const uc = categories.filter(c => c !== cat);
    setCats(uc); sv("v6_cats", uc);
    const uh = habits.filter(h => h.category !== cat);
    setHabits(uh); sv("v6_habits", uh);
    delete CAT[cat];
  }

  // ── gym
  function createSession() {
    const s = { id: Date.now(), date: newSessForm.date, muscle: newSessForm.muscle, exercises: [] };
    const u = [s, ...gymSessions];
    setGym(u); sv("v6_gym", u);
    setAS(s); setSNS(false); setGymView("session");
  }
  function addExercise() {
    if (!newExercise.nombre.trim()) return;
    const ex = { id: Date.now(), nombre: newExercise.nombre.trim(), series: newExercise.series.filter(s => s.reps || s.peso) };
    const u = gymSessions.map(s => s.id === activeSession.id ? { ...s, exercises: [...s.exercises, ex] } : s);
    setGym(u); sv("v6_gym", u); setAS(u.find(s => s.id === activeSession.id));
    setNE({ nombre: "", series: [{ peso: "", reps: "" }] });
  }
  function removeExercise(eid) {
    const u = gymSessions.map(s => s.id === activeSession.id ? { ...s, exercises: s.exercises.filter(e => e.id !== eid) } : s);
    setGym(u); sv("v6_gym", u); setAS(u.find(s => s.id === activeSession.id));
  }
  function deleteSession(id) {
    const u = gymSessions.filter(s => s.id !== id);
    setGym(u); sv("v6_gym", u);
    if (activeSession?.id === id) { setAS(null); setGymView("historial"); }
  }
  const addSerie   = () => setNE(p => ({ ...p, series: [...p.series, { peso: "", reps: "" }] }));
  const updSerie   = (i, f, v) => setNE(p => { const s = [...p.series]; s[i] = { ...s[i], [f]: v }; return { ...p, series: s }; });
  const remSerie   = (i) => setNE(p => ({ ...p, series: p.series.filter((_, j) => j !== i) }));

  // ── schedule
  function saveSlot(day) {
    if (!slotDraft.label.trim()) { setEditSlot(null); return; }
    const key = `${day}__${Date.now()}`;
    const u = { ...schedule, [day]: [...(schedule[day] || []), { ...slotDraft, key }] };
    setSched(u); sv("v6_sched", u); setEditSlot(null); setSD({ label: "", start: "", end: "" });
  }
  function deleteSlot(day, key) {
    const u = { ...schedule, [day]: (schedule[day] || []).filter(s => s.key !== key) };
    setSched(u); sv("v6_sched", u);
  }

  // ── ramos
  function addRamo() {
    if (!newRamo.trim()) return;
    const r = [...ramos, { id: Date.now(), name: newRamo.trim(), tasks: [] }];
    setRamos(r); sv("v6_ramos", r); setNewRamo(""); setSAR(false);
  }
  function removeRamo(id) {
    const r = ramos.filter(r => r.id !== id);
    setRamos(r); sv("v6_ramos", r);
  }
  function addTask(rid) {
    const txt = (newTaskTxt[rid] || "").trim(); if (!txt) return;
    const r = ramos.map(r => r.id === rid ? { ...r, tasks: [...r.tasks, { id: Date.now(), text: txt, done: false }] } : r);
    setRamos(r); sv("v6_ramos", r);
    setNTT(p => ({ ...p, [rid]: "" })); setSAT(p => ({ ...p, [rid]: false }));
  }
  function toggleTask(rid, tid) {
    const r = ramos.map(r => r.id === rid ? { ...r, tasks: r.tasks.map(t => t.id === tid ? { ...t, done: !t.done } : t) } : r);
    setRamos(r); sv("v6_ramos", r);
  }
  function removeTask(rid, tid) {
    const r = ramos.map(r => r.id === rid ? { ...r, tasks: r.tasks.filter(t => t.id !== tid) } : r);
    setRamos(r); sv("v6_ramos", r);
  }

  // ── sleep
  function logSleep() {
    const h = parseFloat(sleepIn);
    if (isNaN(h) || h < 0 || h > 24) return;
    const u = { ...sleepLog, [mk]: { ...(sleepLog[mk] || {}), [today]: h } };
    setSleep(u); sv("v6_sleep", u); setSleepIn("");
  }

  // ── derived
  const gymDaysWeek   = week.filter(d => gymSessions.some(s => s.date === d)).length;
  const monthSleep    = sleepLog[mk] || {};
  const sleepEntries  = Object.entries(monthSleep).sort((a, b) => a[0].localeCompare(b[0]));
  const totalSleep    = sleepEntries.reduce((a, [, h]) => a + h, 0);
  const avgSleep      = sleepEntries.length ? totalSleep / sleepEntries.length : 0;
  const alreadySlept  = !!monthSleep[today];
  const sleepChart    = sleepEntries.map(([d, h]) => ({ day: fmtDate(d), h }));
  const weekStart     = week[0], weekEnd = week[6];
  const daysLeft      = week.filter(d => d >= today).length;

  function catRate(cat) {
    const ch = habits.filter(h => h.category === cat); if (!ch.length) return 0;
    let done = 0, total = 0;
    week.forEach(d => ch.forEach(h => { total++; if (completions[d]?.[h.id]) done++; }));
    return total ? Math.round((done / total) * 100) : 0;
  }
  const overallRate = (() => {
    if (!habits.length) return 0; let done = 0, total = 0;
    week.forEach(d => habits.forEach(h => { total++; if (completions[d]?.[h.id]) done++; }));
    return total ? Math.round((done / total) * 100) : 0;
  })();

  const inp = {
    background: N.surface, border: `0.5px solid ${N.border}`, color: N.text,
    borderRadius: 8, padding: "9px 12px", fontSize: 14, fontFamily: "inherit",
    outline: "none", width: "100%", boxSizing: "border-box",
  };

  const tabs = [
    { id: "dashboard", label: "Inicio" },
    { id: "hoy",       label: "Hoy" },
    { id: "stats",     label: "Progreso" },
    { id: "gimnasio",  label: "Gimnasio" },
    { id: "uni",       label: "Universidad" },
    { id: "dormir",    label: "Dormir" },
    { id: "gestionar", label: "Gestionar" },
  ];

  const metricCard = (label, val, col) => (
    <div key={label} style={{ background: N.surface, borderRadius: 10, padding: "12px", border: `0.5px solid ${N.border}` }}>
      <p style={{ fontSize: 11, color: N.muted, margin: "0 0 4px" }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 500, margin: 0, color: col }}>{val}</p>
    </div>
  );

  return (
    <div style={{ background: N.bg, minHeight: "100vh", fontFamily: "var(--font-sans)", color: N.text, maxWidth: 680, margin: "0 auto", boxSizing: "border-box" }}>

      {/* sticky nav */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: N.bg, borderBottom: `0.5px solid ${N.border}`, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <div style={{ display: "flex", minWidth: "max-content" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "none", border: "none", padding: "12px 14px", fontSize: 13, cursor: "pointer",
              fontFamily: "inherit", whiteSpace: "nowrap",
              color: tab === t.id ? N.uni.neon : N.muted,
              borderBottom: `2px solid ${tab === t.id ? N.uni.neon : "transparent"}`,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "16px 14px 48px" }}>

        {/* ═══ DASHBOARD ═══ */}
        {tab === "dashboard" && (
          <div>
            <p style={{ fontSize: 18, fontWeight: 500, color: N.uni.neon, margin: "0 0 4px" }}>Bienvenido al Dashboard de Martin</p>
            <p style={{ fontSize: 12, color: N.muted, margin: "0 0 16px" }}>Semana {fmtDate(weekStart)} – {fmtDate(weekEnd)} · {daysLeft} día{daysLeft !== 1 ? "s" : ""} restante{daysLeft !== 1 ? "s" : ""}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              {metricCard("Tasa semanal",    `${overallRate}%`,          N.inv.neon)}
              {metricCard("Gym esta semana", `${gymDaysWeek}/4`,          N.gym.neon)}
              {metricCard("Hábitos activos", habits.length,               N.uni.neon)}
              {metricCard("Sueño promedio",  `${avgSleep.toFixed(1)}h`,  N.sleep.neon)}
            </div>

            <p style={{ fontSize: 14, fontWeight: 500, margin: "0 0 8px" }}>Horario semanal</p>
            <div style={{ overflowX: "auto", borderRadius: 8, border: `0.5px solid ${N.border}` }}>
              <table style={{ borderCollapse: "collapse", fontSize: 11, minWidth: 500, width: "100%" }}>
                <thead>
                  <tr style={{ background: N.surface }}>
                    {WEEK_DAYS.map(d => (
                      <th key={d} style={{ padding: "6px 4px", color: N.uni.neon, fontWeight: 500, textAlign: "center", borderBottom: `0.5px solid ${N.border}`, borderLeft: `0.5px solid ${N.border}`, minWidth: 64 }}>
                        <div style={{ marginBottom: 4, fontSize: 10 }}>{d.slice(0, 3)}</div>
                        <button onClick={() => setEditSlot(d === editSlot ? null : d)} style={{ ...abtn(N.uni), padding: "2px 6px", fontSize: 10, borderRadius: 5 }}>+</button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {WEEK_DAYS.some(d => editSlot === d) && (
                    <tr>
                      {WEEK_DAYS.map(d => (
                        <td key={d} style={{ borderLeft: `0.5px solid ${N.border}`, borderTop: `0.5px solid ${N.border}`, padding: "5px 3px", verticalAlign: "top", background: editSlot === d ? N.uni.dim : "transparent" }}>
                          {editSlot === d && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                              <input value={slotDraft.label} onChange={e => setSD(p => ({ ...p, label: e.target.value }))} placeholder="Actividad" style={{ ...inp, padding: "3px 5px", fontSize: 10, borderRadius: 4 }} />
                              <input value={slotDraft.start} onChange={e => setSD(p => ({ ...p, start: e.target.value }))} placeholder="Inicio" style={{ ...inp, padding: "3px 5px", fontSize: 10, borderRadius: 4 }} />
                              <input value={slotDraft.end}   onChange={e => setSD(p => ({ ...p, end:   e.target.value }))} placeholder="Fin"    style={{ ...inp, padding: "3px 5px", fontSize: 10, borderRadius: 4 }} />
                              <div style={{ display: "flex", gap: 3 }}>
                                <button onClick={() => saveSlot(d)} style={{ ...abtn(N.inv), padding: "2px 6px", fontSize: 10, borderRadius: 4 }}>Ok</button>
                                <button onClick={() => { setEditSlot(null); setSD({ label: "", start: "", end: "" }); }} style={{ ...abtn({ neon: N.muted, dim: N.surface, border: N.border }), padding: "2px 6px", fontSize: 10, borderRadius: 4 }}>×</button>
                              </div>
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  )}
                  {WEEK_DAYS.every(d => !(schedule[d]?.length)) && !editSlot ? (
                    <tr><td colSpan={7} style={{ textAlign: "center", padding: "12px", color: N.muted, fontSize: 11 }}>Usa "+" para agregar actividades</td></tr>
                  ) : (
                    (() => {
                      const max = Math.max(...WEEK_DAYS.map(d => (schedule[d] || []).length), 0);
                      return Array.from({ length: max }, (_, i) => (
                        <tr key={i}>
                          {WEEK_DAYS.map(d => {
                            const slot = (schedule[d] || [])[i];
                            return (
                              <td key={d} style={{ borderLeft: `0.5px solid ${N.border}`, borderTop: `0.5px solid ${N.border}`, padding: "3px", verticalAlign: "top" }}>
                                {slot && (
                                  <HoverRow onDelete={() => deleteSlot(d, slot.key)} style={{ background: N.uni.dim, border: `0.5px solid ${N.uni.border}`, borderRadius: 4, padding: "3px 5px" }}>
                                    <div style={{ color: N.uni.neon, fontWeight: 500, fontSize: 10, paddingRight: 16, lineHeight: 1.3 }}>{slot.label}</div>
                                    {(slot.start || slot.end) && (
                                      <div style={{ color: N.muted, fontSize: 9, marginTop: 1 }}>{slot.start}{slot.start && slot.end ? " – " : ""}{slot.end}</div>
                                    )}
                                  </HoverRow>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()
                  )}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 10, color: N.muted, marginTop: 4 }}>Pasa el cursor sobre una actividad para eliminarla.</p>
          </div>
        )}

        {/* ═══ HOY ═══ */}
        {tab === "hoy" && (
          <div>
            <p style={{ fontSize: 16, fontWeight: 500, color: N.text, margin: "0 0 4px" }}>Hoy — {fmtDate(today)}</p>
            <p style={{ fontSize: 12, color: N.muted, margin: "0 0 14px" }}>Semana {fmtDate(weekStart)} – {fmtDate(weekEnd)}</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {["Todas", ...categories].map(c => {
                const col = c === "Todas" ? { neon: N.muted, dim: N.surface, border: N.border } : CAT[c] || N.uni;
                const active = filterCat === c;
                return (
                  <button key={c} onClick={() => setFilter(c)} style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontFamily: "inherit", background: active ? col.dim : "transparent", color: active ? col.neon : N.muted, border: `0.5px solid ${active ? col.border : N.border}` }}>{c}</button>
                );
              })}
            </div>
            {categories.filter(c => filterCat === "Todas" || filterCat === c).map(cat => {
              const ch = habits.filter(h => h.category === cat && (filterCat === "Todas" || filterCat === cat));
              if (!ch.length) return null;
              const col = CAT[cat] || N.uni;
              return (
                <div key={cat} style={{ marginBottom: 18 }}>
                  <p style={{ ...pill(col), marginBottom: 8 }}>{cat}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {ch.map(h => {
                      const done   = !!completions[today]?.[h.id];
                      const streak = computeStreak(completions, h.id);
                      return (
                        <div key={h.id} onClick={() => toggleHabit(h.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 13px", borderRadius: 10, cursor: "pointer", background: done ? col.dim : N.surface, border: `0.5px solid ${done ? col.border : N.border}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: done ? col.neon : "transparent", border: `1.5px solid ${done ? col.neon : N.borderHover}` }}>
                              {done && <span style={{ color: "#000", fontSize: 13, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                            </div>
                            <span style={{ fontSize: 14, color: done ? col.neon : N.text }}>{h.name}</span>
                          </div>
                          {streak > 0 && <span style={{ ...pill(N.streak), fontSize: 11 }}>{streak}d</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ PROGRESO ═══ */}
        {tab === "stats" && (
          <div>
            <p style={{ fontSize: 16, fontWeight: 500, color: N.text, margin: "0 0 4px" }}>Progreso semanal</p>
            <p style={{ fontSize: 12, color: N.muted, margin: "0 0 14px" }}>{fmtDate(weekStart)} – {fmtDate(weekEnd)}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              {metricCard("Tasa semanal",    `${overallRate}%`,         N.inv.neon)}
              {metricCard("Gym esta semana", `${gymDaysWeek}/4`,         N.gym.neon)}
              {metricCard("Hábitos activos", habits.length,              N.uni.neon)}
              {metricCard("Sueño promedio",  `${avgSleep.toFixed(1)}h`, N.sleep.neon)}
            </div>

            <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 8px" }}>Hábitos — semana actual</p>
            <div style={{ overflowX: "auto", background: N.surface, borderRadius: 8, border: `0.5px solid ${N.border}`, padding: "10px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 340 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "3px 6px", color: N.muted, fontWeight: 400 }}>Hábito</th>
                    {week.map((d, i) => (
                      <th key={d} style={{ textAlign: "center", padding: "3px 4px", color: d === today ? N.uni.neon : N.muted, fontWeight: d === today ? 500 : 400 }}>{DAYS_SHORT[i]}</th>
                    ))}
                    <th style={{ textAlign: "center", padding: "3px 6px", color: N.muted, fontWeight: 400 }}>Racha</th>
                  </tr>
                </thead>
                <tbody>
                  {habits.map(h => {
                    const col    = CAT[h.category] || N.uni;
                    const streak = computeStreak(completions, h.id);
                    return (
                      <tr key={h.id} style={{ borderTop: `0.5px solid ${N.border}` }}>
                        <td style={{ padding: "5px 6px", color: N.text, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</td>
                        {week.map(d => (
                          <td key={d} style={{ textAlign: "center", padding: "5px 4px" }}>
                            {completions[d]?.[h.id]
                              ? <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 3, background: col.neon }} />
                              : <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 3, background: N.bg, border: `0.5px solid ${N.border}` }} />}
                          </td>
                        ))}
                        <td style={{ textAlign: "center", padding: "5px 6px", color: streak > 0 ? N.streak.neon : N.muted }}>{streak > 0 ? `${streak}d` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p style={{ fontSize: 13, fontWeight: 500, margin: "16px 0 8px" }}>Completación por categoría</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {categories.map(cat => {
                const rate = catRate(cat), col = CAT[cat] || N.uni;
                return (
                  <div key={cat}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: N.muted }}>{cat}</span>
                      <span style={{ color: col.neon, fontWeight: 500 }}>{rate}%</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: N.surface, border: `0.5px solid ${N.border}`, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${rate}%`, background: col.neon, borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: N.muted }}>Gym (asistencia)</span>
                  <span style={{ color: N.gym.neon, fontWeight: 500 }}>{gymDaysWeek}/4 días</span>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: N.surface, border: `0.5px solid ${N.border}`, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(gymDaysWeek / 4 * 100, 100)}%`, background: N.gym.neon, borderRadius: 3 }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ GIMNASIO ═══ */}
        {tab === "gimnasio" && (
          <div>
            <div style={{ display: "flex", gap: 0, marginBottom: 14, borderBottom: `0.5px solid ${N.border}` }}>
              {["historial", "session"].map(v => (
                <button key={v} onClick={() => setGymView(v)} style={{ background: "none", border: "none", padding: "8px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", color: gymView === v ? N.gym.neon : N.muted, borderBottom: `2px solid ${gymView === v ? N.gym.neon : "transparent"}`, marginBottom: -1 }}>
                  {v === "historial" ? "Historial" : activeSession ? `Sesión — ${activeSession.muscle}` : "Sesión activa"}
                </button>
              ))}
            </div>

            {gymView === "historial" && (
              <div>
                {showNewSess ? (
                  <div style={{ background: N.surface, borderRadius: 10, border: `0.5px solid ${N.gym.border}`, padding: "14px", marginBottom: 14 }}>
                    <p style={{ fontSize: 14, fontWeight: 500, color: N.gym.neon, margin: "0 0 12px" }}>Nueva sesión</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, color: N.muted, display: "block", marginBottom: 4 }}>Grupo muscular</label>
                        <select value={newSessForm.muscle} onChange={e => setNSF(p => ({ ...p, muscle: e.target.value }))} style={{ ...inp, padding: "8px 10px" }}>
                          {MUSCLE_GROUPS.map(m => <option key={m}>{m}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: N.muted, display: "block", marginBottom: 4 }}>Fecha</label>
                        <input type="date" value={newSessForm.date} onChange={e => setNSF(p => ({ ...p, date: e.target.value }))} style={inp} />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={createSession} style={abtn(N.gym)}>Iniciar sesión</button>
                      <button onClick={() => setSNS(false)} style={abtn({ neon: N.muted, dim: N.surface, border: N.border })}>Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setSNS(true)} style={{ ...abtn(N.gym), marginBottom: 14 }}>+ Nueva sesión</button>
                )}
                {gymSessions.length === 0
                  ? <p style={{ fontSize: 13, color: N.muted }}>Sin sesiones registradas.</p>
                  : gymSessions.map(s => (
                    <HoverRow key={s.id} onDelete={() => deleteSession(s.id)} style={{ background: N.surface, borderRadius: 10, border: `0.5px solid ${N.border}`, padding: "12px", marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 28, marginBottom: s.exercises.length ? 8 : 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={pill(N.gym)}>{s.muscle}</span>
                          <span style={{ fontSize: 12, color: N.muted }}>{fmtDate(s.date)}</span>
                        </div>
                        <button onClick={() => { setAS(s); setGymView("session"); }} style={{ ...abtn(N.gym), padding: "4px 10px", fontSize: 12 }}>Ver</button>
                      </div>
                      {s.exercises.map(ex => (
                        <div key={ex.id} style={{ fontSize: 12, color: N.muted, marginTop: 4 }}>
                          <span style={{ color: N.gym.neon }}>{ex.nombre}</span>
                          <span style={{ marginLeft: 6 }}>{ex.series.map((s, i) => `S${i + 1}:${s.peso || "—"}kg×${s.reps || "—"}`).join(" | ")}</span>
                        </div>
                      ))}
                    </HoverRow>
                  ))
                }
              </div>
            )}

            {gymView === "session" && activeSession && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <span style={pill(N.gym)}>{activeSession.muscle}</span>
                  <span style={{ fontSize: 12, color: N.muted }}>{fmtDate(activeSession.date)}</span>
                </div>
                {activeSession.exercises.map(ex => (
                  <div key={ex.id} style={{ background: N.surface, borderRadius: 10, border: `0.5px solid ${N.border}`, padding: "10px 12px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: N.gym.neon }}>{ex.nombre}</span>
                      <button onClick={() => removeExercise(ex.id)} style={{ background: "none", border: "none", color: N.muted, cursor: "pointer", fontSize: 16 }}>×</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {ex.series.map((s, i) => (
                        <span key={i} style={{ ...pill(N.gym), fontSize: 10 }}>S{i + 1}: {s.peso || "—"}kg × {s.reps || "—"}r</span>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{ background: N.surface, borderRadius: 10, border: `0.5px solid ${N.gym.border}`, padding: "14px", marginTop: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: N.gym.neon, margin: "0 0 10px" }}>Agregar ejercicio</p>
                  <input value={newExercise.nombre} onChange={e => setNE(p => ({ ...p, nombre: e.target.value }))} placeholder="Nombre del ejercicio" style={{ ...inp, marginBottom: 10 }} />
                  {newExercise.series.map((s, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "24px 1fr 1fr 24px", gap: 8, alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: N.muted }}>S{i + 1}</span>
                      <input type="number" min="0" value={s.peso} onChange={e => updSerie(i, "peso", e.target.value)} placeholder="kg"   style={inp} />
                      <input type="number" min="0" value={s.reps} onChange={e => updSerie(i, "reps", e.target.value)} placeholder="reps" style={inp} />
                      <button onClick={() => remSerie(i)} style={{ background: "none", border: "none", color: N.muted, cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button onClick={addSerie}    style={abtn({ neon: N.muted, dim: N.surface, border: N.border })}>+ Serie</button>
                    <button onClick={addExercise} style={abtn(N.gym)}>Guardar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ UNIVERSIDAD ═══ */}
        {tab === "uni" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <p style={{ fontSize: 16, fontWeight: 500, color: N.uni.neon, margin: 0 }}>Cosas que hacer</p>
              <button onClick={() => setSAR(p => !p)} style={{ ...abtn(N.uni), padding: "6px 12px", fontSize: 12 }}>+ Agregar Ramo</button>
            </div>
            {showAddRamo && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <input value={newRamo} onChange={e => setNewRamo(e.target.value)} onKeyDown={e => e.key === "Enter" && addRamo()} placeholder="Ej: Cálculo Avanzado" style={inp} />
                <button onClick={addRamo} style={abtn(N.uni)}>Crear</button>
                <button onClick={() => setSAR(false)} style={abtn({ neon: N.muted, dim: N.surface, border: N.border })}>×</button>
              </div>
            )}
            {ramos.length === 0 && <p style={{ fontSize: 13, color: N.muted }}>Sin ramos. Agrega uno para comenzar.</p>}
            {ramos.map(ramo => (
              <div key={ramo.id} style={{ marginBottom: 16 }}>
                <HoverRow onDelete={() => removeRamo(ramo.id)} style={{ background: N.uni.dim, border: `0.5px solid ${N.uni.border}`, borderRadius: 10, padding: "10px 13px", marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 28 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: N.uni.neon }}>{ramo.name}</span>
                    <button onClick={() => setSAT(p => ({ ...p, [ramo.id]: !p[ramo.id] }))} style={{ ...abtn(N.uni), padding: "4px 10px", fontSize: 12 }}>+ Tarea</button>
                  </div>
                </HoverRow>
                {showAddTask[ramo.id] && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 6, marginLeft: 12 }}>
                    <input value={newTaskTxt[ramo.id] || ""} onChange={e => setNTT(p => ({ ...p, [ramo.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && addTask(ramo.id)} placeholder="Nombre de la tarea..." style={{ ...inp, fontSize: 13 }} />
                    <button onClick={() => addTask(ramo.id)} style={abtn(N.uni)}>+</button>
                    <button onClick={() => setSAT(p => ({ ...p, [ramo.id]: false }))} style={abtn({ neon: N.muted, dim: N.surface, border: N.border })}>×</button>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 5, marginLeft: 12 }}>
                  {ramo.tasks.length === 0 && <p style={{ fontSize: 12, color: N.muted, margin: "2px 0" }}>Sin tareas aún.</p>}
                  {ramo.tasks.map(task => (
                    <HoverRow key={task.id} onDelete={() => removeTask(ramo.id, task.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: task.done ? N.inv.dim : N.surface, border: `0.5px solid ${task.done ? N.inv.border : N.border}`, borderRadius: 8 }}>
                      <div onClick={() => toggleTask(ramo.id, task.id)} style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: task.done ? N.inv.neon : "transparent", border: `1.5px solid ${task.done ? N.inv.neon : N.borderHover}` }}>
                        {task.done && <span style={{ color: "#000", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 13, color: task.done ? N.inv.neon : N.text, textDecoration: task.done ? "line-through" : "none", flex: 1, paddingRight: 24 }}>{task.text}</span>
                    </HoverRow>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ═══ DORMIR ═══ */}
        {tab === "dormir" && (
          <div>
            <p style={{ fontSize: 16, fontWeight: 500, color: N.sleep.neon, margin: "0 0 4px" }}>Registro de sueño</p>
            <p style={{ fontSize: 12, color: N.muted, margin: "0 0 14px" }}>{mk} · se reinicia cada 1° del mes</p>
            <div style={{ background: N.surface, borderRadius: 10, border: `0.5px solid ${N.sleep.border}`, padding: "14px", marginBottom: 14 }}>
              <p style={{ fontSize: 13, color: "#c4b5fd", margin: "0 0 10px" }}>¿Cuántas horas dormiste anoche?</p>
              {alreadySlept ? (
                <p style={{ fontSize: 13, color: N.inv.neon, margin: 0 }}>Ya registraste: <strong>{monthSleep[today]}h</strong></p>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="number" min="0" max="24" step="0.5" value={sleepIn} onChange={e => setSleepIn(e.target.value)} onKeyDown={e => e.key === "Enter" && logSleep()} placeholder="7.5" style={{ ...inp, width: 90 }} />
                  <span style={{ fontSize: 13, color: N.muted }}>horas</span>
                  <button onClick={logSleep} style={abtn(N.sleep)}>Registrar</button>
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              {metricCard("Horas promedio",   `${avgSleep.toFixed(1)}h`,               N.sleep.neon)}
              {metricCard("Horas totales",    `${totalSleep.toFixed(1)}h`,             N.sleep.neon)}
              {metricCard("Días registrados", `${sleepEntries.length}/${daysInMonth()}`, N.text)}
            </div>
            <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 8px" }}>Historial del mes</p>
            <div style={{ background: N.surface, borderRadius: 10, border: `0.5px solid ${N.border}`, padding: "10px" }}>
              <SleepChart data={sleepChart} />
              <p style={{ fontSize: 10, color: N.muted, marginTop: 4 }}>— línea amarilla = tendencia</p>
            </div>
            {sleepEntries.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 5 }}>
                {[...sleepEntries].reverse().map(([d, h]) => (
                  <div key={d} style={{ display: "flex", justifyContent: "space-between", padding: "7px 12px", background: N.surface, borderRadius: 7, border: `0.5px solid ${N.border}`, fontSize: 13 }}>
                    <span style={{ color: N.muted }}>{fmtDate(d)}</span>
                    <span style={{ color: N.sleep.neon, fontWeight: 500 }}>{h}h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ GESTIONAR ═══ */}
        {tab === "gestionar" && (
          <div>
            <p style={{ fontSize: 14, fontWeight: 500, margin: "0 0 10px" }}>Categorías</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
              {categories.map(cat => {
                const col    = CAT[cat] || N.uni;
                const isBase = !!BASE_CATS[cat];
                return (
                  <div key={cat} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 13px", borderRadius: 8, background: col.dim || N.surface, border: `0.5px solid ${col.border || N.border}`, fontSize: 13 }}>
                    <span style={{ color: col.neon }}>{cat}</span>
                    {isBase
                      ? <span style={{ fontSize: 11, color: N.muted }}>predeterminada</span>
                      : <button onClick={() => removeCategory(cat)} style={{ background: "none", border: "none", color: "#e24b4a", cursor: "pointer", fontSize: 18, padding: "0 4px", lineHeight: 1 }}>×</button>}
                  </div>
                );
              })}
            </div>
            {showAddCat ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <input value={newCatName} onChange={e => setNCN(e.target.value)} onKeyDown={e => e.key === "Enter" && addCategory()} placeholder="Nueva categoría" style={inp} />
                <button onClick={addCategory} style={abtn(N.streak)}>Crear</button>
                <button onClick={() => { setSAC(false); setNCN(""); }} style={abtn({ neon: N.muted, dim: N.surface, border: N.border })}>×</button>
              </div>
            ) : (
              <button onClick={() => setSAC(true)} style={{ ...abtn(N.streak), marginBottom: 20 }}>+ Nueva categoría</button>
            )}

            <p style={{ fontSize: 14, fontWeight: 500, margin: "0 0 10px" }}>Agregar hábito</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              <input value={newHabit.name} onChange={e => setNewHabit(p => ({ ...p, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && addHabit()} placeholder="Nombre del hábito" style={inp} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                <select value={newHabit.category} onChange={e => setNewHabit(p => ({ ...p, category: e.target.value }))} style={{ ...inp, padding: "8px 10px" }}>
                  {categories.map(c => <option key={c}>{c}</option>)}
                </select>
                <button onClick={addHabit} style={{ ...abtn(N.streak), height: 40 }}>Agregar</button>
              </div>
            </div>

            <p style={{ fontSize: 14, fontWeight: 500, margin: "0 0 10px" }}>Hábitos actuales</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {habits.map(h => {
                const col = CAT[h.category] || N.uni;
                return (
                  <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 13px", borderRadius: 8, background: N.surface, border: `0.5px solid ${N.border}`, fontSize: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                      <span style={{ ...pill(col), flexShrink: 0 }}>{h.category}</span>
                      <span style={{ color: N.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</span>
                    </div>
                    <button onClick={() => removeHabit(h.id)} style={{ background: "none", border: "none", color: N.muted, cursor: "pointer", fontSize: 18, padding: "0 0 0 8px", flexShrink: 0 }}>×</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}